
import React, { useState, useEffect, useLayoutEffect, Suspense, lazy } from 'react';
import { RouterContext, matchPath, Navigate } from './routes';
import { Layout } from './components/common/Layout';
import { PublicRouteThemeSync } from './components/common/ThemeContext';
import { ClerkLoginView } from './components/common/ClerkLoginView';
import { ClerkSignUpView } from './components/common/ClerkSignUpView';
import { CompleteOnboardingView } from './components/common/CompleteOnboardingView';
import { OperatorFirstRunShell } from './components/common/OperatorFirstRunShell';
import { LandingView } from './components/common/LandingView';
/** Lazy chunk only when demo is compiled in (`VITE_DEMO_MODE=1`). Demo host deploys must set this at build time. */
const DemoPersonaPicker =
  import.meta.env.VITE_DEMO_MODE === '1'
    ? lazy(() => import('./components/demo/DemoPersonaPicker').then(m => ({ default: m.DemoPersonaPicker })))
    : null;
const OrgSelectionView = lazy(() => import('./components/common/OrgSelectionView').then(m => ({ default: m.OrgSelectionView })));
import { MANAGEMENT_ROLES, ROLES } from '@repo/shared-config';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { useAppStore } from './store';
import { usePostHog } from 'posthog-js/react';
import { ROUTES } from '@repo/shared-types/routeRegistry';
// Guards & Utils (Eagerly loaded - used on every page)
import { AuthGuard } from './components/common/AuthGuard';
import { RoleBasedHome } from './components/common/RoleBasedHome';
import { UnauthorizedView } from './components/common/UnauthorizedView';
import { NotFoundView } from './components/common/NotFoundView';
import { ActivityView } from './components/common/ActivityView';
import { logger } from '@repo/shared-utils';
import { writeHistory, installDevHistoryGuards } from './navigation/writeHistory';
import { syncRouterFromWindow } from './navigation/routerStore';
import { resolveCanonicalUrl } from './navigation/resolveCanonicalUrl';
import { isSafePathname } from './navigation/isSafePathname';
import { isDemoMode } from './utils/demoClock';

// Loading component for Suspense boundaries
const RouteLoading = () => (
  <div className="flex items-center justify-center h-full min-h-[400px]">
    <LoadingSpinner size="lg" />
  </div>
);

// Lazy-loaded views grouped by feature area for better code splitting
// Maintenance Module
const MaintenanceView = lazy(() => import('./components/maintenance/MaintenanceView').then(m => ({ default: m.MaintenanceView })));
const ProjectsView = lazy(() => import('./components/projects/ProjectsView').then(m => ({ default: m.ProjectsView })));
const ProjectDetailView = lazy(() => import('./components/projects/ProjectDetailView').then(m => ({ default: m.ProjectDetailView })));
const VendorMarketplace = lazy(() => import('./components/vendor/VendorMarketplace').then(m => ({ default: m.VendorMarketplace })));
const VendorDirectoryView = lazy(() => import('./components/vendor/VendorDirectoryView').then(m => ({ default: m.VendorDirectoryView })));
const SOPGenerator = lazy(() => import('./components/maintenance/SOPGenerator').then(m => ({ default: m.SOPGenerator })));
const TurnBoardView = lazy(() => import('./components/maintenance/TurnBoardView').then(m => ({ default: m.TurnBoardView })));
const InspectionsView = lazy(() => import('./components/inspections/InspectionsView').then(m => ({ default: m.InspectionsView })));
const InspectionSchedulingView = lazy(() => import('./components/inspections/InspectionSchedulingView').then(m => ({ default: m.InspectionSchedulingView })));

// Leasing Module
const LeasingDashboardView = lazy(() => import('./components/leasing/LeasingDashboardView').then(m => ({ default: m.LeasingDashboardView })));
const GuestCardsView = lazy(() => import('./components/leasing/GuestCardsView').then(m => ({ default: m.GuestCardsView })));
const ApplicationsView = lazy(() => import('./components/leasing/ApplicationsView').then(m => ({ default: m.ApplicationsView })));
const LeaseManagementView = lazy(() => import('./components/leasing/LeaseManagementView').then(m => ({ default: m.LeaseManagementView })));
const LeasingOnboardingView = lazy(() => import('./components/leasing/LeasingOnboardingView').then(m => ({ default: m.LeasingOnboardingView })));
const MarketingDashboardView = lazy(() => import('./components/leasing/MarketingDashboardView').then(m => ({ default: m.MarketingDashboardView })));

// Accounting Module
const AccountingBillsView = lazy(() => import('./components/accounting/AccountingBillsView').then(m => ({ default: m.AccountingBillsView })));
const RecurringBillsView = lazy(() => import('./components/accounting/RecurringBillsView').then(m => ({ default: m.RecurringBillsView })));
const AccountingPaymentsView = lazy(() => import('./components/accounting/AccountingPaymentsView').then(m => ({ default: m.AccountingPaymentsView })));
const GeneralLedgerView = lazy(() => import('./components/accounting/GeneralLedgerView').then(m => ({ default: m.GeneralLedgerView })));
const ChartOfAccountsView = lazy(() => import('./components/accounting/ChartOfAccountsView').then(m => ({ default: m.ChartOfAccountsView })));
const JournalEntryForm = lazy(() => import('./components/accounting/JournalEntryForm').then(m => ({ default: m.JournalEntryForm })));
const BankReconciliationView = lazy(() => import('./components/accounting/BankReconciliationView').then(m => ({ default: m.BankReconciliationView })));
const CAMTrackingView = lazy(() => import('./components/property/CAMTrackingView').then(m => ({ default: m.CAMTrackingView })));
const MonthEndCloseView = lazy(() => import('./components/accounting/MonthEndCloseView').then(m => ({ default: m.MonthEndCloseView })));
const ManagementFeeSweepView = lazy(() =>
  import('./components/accounting/ManagementFeeSweepView').then((m) => ({ default: m.ManagementFeeSweepView })),
);
const OwnerDistributionView = lazy(() =>
  import('./components/accounting/OwnerDistributionView').then((m) => ({ default: m.OwnerDistributionView })),
);
const DistributionsListView = lazy(() => import('./components/accounting/DistributionsListView').then(m => ({ default: m.DistributionsListView })));

// Properties Module
const PropertyDirectoryView = lazy(() => import('./components/property/PropertyDirectoryView').then(m => ({ default: m.PropertyDirectoryView })));
const PropertyDetailsView = lazy(() => import('./components/property/PropertyDetailsView').then(m => ({ default: m.PropertyDetailsView })));
const UnitManagementView = lazy(() => import('./components/property/UnitManagementView').then(m => ({ default: m.UnitManagementView })));
const PortfolioView = lazy(() => import('./components/property/PortfolioView').then(m => ({ default: m.PortfolioView })));

// Tenants Module
const TenantDirectoryView = lazy(() => import('./components/tenant/TenantDirectoryView').then(m => ({ default: m.TenantDirectoryView })));
const TenantProfileView = lazy(() => import('./components/tenant/TenantProfileView').then(m => ({ default: m.TenantProfileView })));

// Owners Module
const OwnerAccessView = lazy(() => import('./components/owner/OwnerAccessView').then(m => ({ default: m.OwnerAccessView })));
const OwnerProfileView = lazy(() => import('./components/owner/OwnerProfileView').then(m => ({ default: m.OwnerProfileView })));
const OwnerStatementsView = lazy(() => import('./components/owner/OwnerStatementsView').then(m => ({ default: m.OwnerStatementsView })));
const OwnerCommunicationView = lazy(() => import('./components/owner/OwnerCommunicationView').then(m => ({ default: m.OwnerCommunicationView })));
const OwnerDocumentsView = lazy(() => import('./components/owner/OwnerDocumentsView').then(m => ({ default: m.OwnerDocumentsView })));
const OwnerSettingsView = lazy(() => import('./components/owner/OwnerSettingsView').then(m => ({ default: m.OwnerSettingsView })));
const DocumentLibraryView = lazy(() => import('./components/common/DocumentLibraryView').then(m => ({ default: m.DocumentLibraryView })));

// Admin / Ops Module
const WorkflowsView = lazy(() => import('./components/common/WorkflowsView').then(m => ({ default: m.WorkflowsView })));
const ReportsLibraryView = lazy(() => import('./components/reports/ReportsLibraryView').then(m => ({ default: m.ReportsLibraryView })));
const ScheduledReportsView = lazy(() => import('./components/reports/ScheduledReportsView').then(m => ({ default: m.ScheduledReportsView })));
const SettingsView = lazy(() => import('./components/common/SettingsView').then(m => ({ default: m.SettingsView })));
const IntegrationSettingsView = lazy(() => import('./components/common/IntegrationSettingsView').then(m => ({ default: m.IntegrationSettingsView })));
const AdminAuditView = lazy(() => import('./components/admin/AdminAuditView').then(m => ({ default: m.AdminAuditView })));
// Compliance Module - Full structure
const ComplianceOverviewView = lazy(() => import('./components/compliance/ComplianceOverviewView').then(m => ({ default: m.ComplianceOverviewView })));
const ComplianceInspectionsView = lazy(() => import('./components/compliance/ComplianceInspectionsView').then(m => ({ default: m.ComplianceInspectionsView })));
const ComplianceHCVView = lazy(() => import('./components/compliance/ComplianceHCVView').then(m => ({ default: m.ComplianceHCVView })));
const ComplianceObligationsView = lazy(() => import('./components/compliance/ComplianceObligationsView').then(m => ({ default: m.ComplianceObligationsView })));
const GovernanceLogsView = lazy(() => import('./components/compliance/GovernanceLogsView').then(m => ({ default: m.GovernanceLogsView })));
const PMCalendarView = lazy(() => import('./components/operations/PMCalendarView').then(m => ({ default: m.PMCalendarView })));
const WorkQueueView = lazy(() => import('./components/operations/WorkQueueView').then(m => ({ default: m.WorkQueueView })));
const ExceptionQueueView = lazy(() => import('./components/operations/ExceptionQueueView').then(m => ({ default: m.ExceptionQueueView })));

// Tenant Portal
const TenantPaymentsView = lazy(() => import('./components/tenant/TenantPaymentsView').then(m => ({ default: m.TenantPaymentsView })));
const TenantDocumentsView = lazy(() => import('./components/tenant/TenantDocumentsView').then(m => ({ default: m.TenantDocumentsView })));
const TenantOnboardingView = lazy(() => import('./components/tenant/TenantOnboardingView').then(m => ({ default: m.TenantOnboardingView })));
const TenantLeaseWizard = lazy(() => import('./components/leasing/TenantLeaseWizard').then(m => ({ default: m.TenantLeaseWizard })));
const TenantCommunicationView = lazy(() => import('./components/tenant/TenantCommunicationView').then(m => ({ default: m.TenantCommunicationView })));
const TenantSettingsView = lazy(() => import('./components/tenant/TenantSettingsView').then(m => ({ default: m.TenantSettingsView })));
const CommunityView = lazy(() => import('./components/common/CommunityView').then(m => ({ default: m.CommunityView })));

// Vendor Portal
const VendorJobView = lazy(() => import('./components/vendor/VendorJobView').then(m => ({ default: m.VendorJobView })));
const VendorJobsOverviewView = lazy(() => import('./components/vendor/VendorJobsOverviewView').then(m => ({ default: m.VendorJobsOverviewView })));
const VendorProfileView = lazy(() => import('./components/vendor/VendorProfileView').then(m => ({ default: m.VendorProfileView })));
const VendorMapView = lazy(() => import('./components/vendor/VendorMapView').then(m => ({ default: m.VendorMapView })));
const VendorCommunicationView = lazy(() => import('./components/vendor/VendorCommunicationView').then(m => ({ default: m.VendorCommunicationView })));
const VendorJobHistoryView = lazy(() => import('./components/vendor/VendorJobHistoryView').then(m => ({ default: m.VendorJobHistoryView })));
const VendorActiveJobsView = lazy(() => import('./components/vendor/VendorActiveJobsView').then(m => ({ default: m.VendorActiveJobsView })));
const VendorPortfolioView = lazy(() => import('./components/vendor/VendorPortfolioView').then(m => ({ default: m.VendorPortfolioView })));
const VendorWorkPhotosView = lazy(() => import('./components/vendor/VendorWorkPhotosView').then(m => ({ default: m.VendorWorkPhotosView })));
const VendorCertificationsView = lazy(() => import('./components/vendor/VendorCertificationsView').then(m => ({ default: m.VendorCertificationsView })));
const VendorFinancialsView = lazy(() => import('./components/vendor/VendorFinancialsView').then(m => ({ default: m.VendorFinancialsView })));
const VendorInvoicesView = lazy(() => import('./components/vendor/VendorInvoicesView').then(m => ({ default: m.VendorInvoicesView })));
const VendorPaymentsView = lazy(() => import('./components/vendor/VendorPaymentsView').then(m => ({ default: m.VendorPaymentsView })));
const VendorEarningsView = lazy(() => import('./components/vendor/VendorEarningsView').then(m => ({ default: m.VendorEarningsView })));
const VendorCalendarView = lazy(() => import('./components/vendor/VendorCalendarView').then(m => ({ default: m.VendorCalendarView })));
const VendorPerformanceView = lazy(() => import('./components/vendor/VendorPerformanceView').then(m => ({ default: m.VendorPerformanceView })));
const VendorBusinessInfoView = lazy(() => import('./components/vendor/VendorBusinessInfoView').then(m => ({ default: m.VendorBusinessInfoView })));
const VendorReviewsView = lazy(() => import('./components/vendor/VendorReviewsView').then(m => ({ default: m.VendorReviewsView })));
const VendorSettingsView = lazy(() => import('./components/vendor/VendorSettingsView').then(m => ({ default: m.VendorSettingsView })));

// Investor Portal — launch-deferred (routes removed; components kept on disk for later).

// Communications (shared across roles)
const CommunicationsView = lazy(() => import('./components/common/CommunicationsView').then(m => ({ default: m.CommunicationsView })));

// Notes - Centralized view
const NotesListView = lazy(() => import('./components/common/NotesListView').then(m => ({ default: m.NotesListView })));

// Public
const PublicWebsiteView = lazy(() => import('./components/common/PublicWebsiteView'));
const PublicApplicationForm = lazy(() => import('./components/leasing/PublicApplicationForm').then(m => ({ default: m.PublicApplicationForm })));

// Legal (unauthenticated)
const PrivacyPolicyView = lazy(() => import('./components/legal/PrivacyPolicyView').then(m => ({ default: m.PrivacyPolicyView })));
const TermsOfServiceView = lazy(() => import('./components/legal/TermsOfServiceView').then(m => ({ default: m.TermsOfServiceView })));
const RetentionPolicyView = lazy(() => import('./components/legal/RetentionPolicyView').then(m => ({ default: m.RetentionPolicyView })));
const SubprocessorsView = lazy(() => import('./components/legal/SubprocessorsView').then(m => ({ default: m.SubprocessorsView })));

const isWebTest = import.meta.env.MODE === 'test';

/**
 * Seed in-memory `path` from the address bar after canonical redirects so the first
 * authenticated render matches `resolveCanonicalUrl` (avoids one-frame NotFound on legacy URLs).
 */
function getInitialPathFromWindow(): string {
  const raw = window.location.pathname;
  if (!isSafePathname(raw)) {
    return '/';
  }
  const store = useAppStore.getState();
  const hasBackendProfile = Boolean(store.currentUserId?.trim());
  const authed = isWebTest ? hasBackendProfile : store.clerkSignedIn && hasBackendProfile;
  if (!authed || !raw.startsWith('/app')) {
    return raw;
  }
  const r = resolveCanonicalUrl({
    pathname: raw,
    search: window.location.search,
    user: { isSelfManagingOwner: Boolean(store.isSelfManagingOwner) },
  });
  if (r.action === 'redirect') {
    const full = `${r.pathname}${r.search}`;
    try {
      window.history.replaceState(null, '', full);
    } catch {
      /* sandboxed env */
    }
    return r.pathname.split('?')[0].split('#')[0];
  }
  return raw;
}

export const AppRouter = () => {
  const posthog = usePostHog();
  const clerkSignedIn = useAppStore((s) => s.clerkSignedIn);
  const isAuthPending = useAppStore((s) => s.isAuthPending);
  const currentUserId = useAppStore((s) => s.currentUserId);
  const isSelfManagingOwner = useAppStore((s) => s.isSelfManagingOwner);
  const hasBackendProfile = Boolean(currentUserId?.trim());
  const isAuthenticated = isWebTest ? hasBackendProfile : clerkSignedIn && hasBackendProfile;

  useLayoutEffect(() => {
    installDevHistoryGuards();
    syncRouterFromWindow();
  }, []);

  useLayoutEffect(() => {
    if (!isAuthenticated) return;
    const r = resolveCanonicalUrl({
      pathname: window.location.pathname,
      search: window.location.search,
      user: { isSelfManagingOwner: Boolean(isSelfManagingOwner) },
    });
    if (r.action === 'redirect') {
      writeHistory(() => {
        window.history.replaceState(null, '', `${r.pathname}${r.search}`);
      });
      const pathnameOnly = r.pathname.split('?')[0].split('#')[0];
      setPath(pathnameOnly);
    }
  }, [isAuthenticated, isSelfManagingOwner]);

  // Initialize path with safety check for sandboxed environments where pathname might be a full URL or blob
  const [path, setPath] = useState(() => getInitialPathFromWindow());

  // Single popstate listener for back/forward (do not depend on path to avoid re-running and log spam)
  useEffect(() => {
    const handler = () => {
      const p = window.location.pathname;
      if (!isSafePathname(p)) return;
      const search = window.location.search;
      if (isAuthenticated && p.startsWith('/app')) {
        const r = resolveCanonicalUrl({
          pathname: p,
          search,
          user: { isSelfManagingOwner: Boolean(isSelfManagingOwner) },
        });
        if (r.action === 'redirect') {
          writeHistory(() => {
            window.history.replaceState(null, '', `${r.pathname}${r.search}`);
          });
          setPath(r.pathname.split('?')[0].split('#')[0]);
          syncRouterFromWindow();
          return;
        }
      }
      setPath(p);
      syncRouterFromWindow();
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [isAuthenticated, isSelfManagingOwner]);

  // Track pageviews in PostHog when path changes
  useEffect(() => {
    if (posthog && path) {
      // Extract route metadata for better analytics
      const isAppRoute = path.startsWith(ROUTES.APP);
      const isPublicRoute = path.startsWith(ROUTES.PUBLIC_SITE);
      const routeType = isAppRoute ? 'app' : isPublicRoute ? 'public' : 'auth';

      const routeParts = path.split('/').filter(Boolean);
      const routeModule = routeParts.length > 1 ? routeParts[1] : routeParts[0] || 'root';

      // Extract route segment info (useful for identifying dynamic routes)
      const routeSegments = routeParts.length;
      const hasDynamicSegment = routeParts.some(part => /^[a-f0-9-]{36}$/i.test(part) || /^\d+$/.test(part));

      // Build pageview properties
      const pageviewProperties: Record<string, unknown> = {
        path: path,
        route_type: routeType,
        route_module: routeModule,
        route_segments: routeSegments,
        has_dynamic_segment: hasDynamicSegment,
      };

      // Match dynamic routes for categorization using registry patterns
      // Map base route patterns to their semantic categories
      const dynamicRouteMappings = [
        { pattern: ROUTES.PROPERTY_DETAIL, category: 'property_detail' },
        { pattern: ROUTES.TENANT_DETAIL, category: 'tenant_detail' },
        { pattern: ROUTES.OWNER_DETAIL, category: 'owner_detail' },
        { pattern: ROUTES.PROJECT_DETAIL, category: 'project_detail' },
        { pattern: ROUTES.VENDOR_JOB_DETAIL, category: 'job_detail' },
        { pattern: ROUTES.INVESTMENT_DETAIL, category: 'investment_detail' },
        { pattern: ROUTES.VENDOR_DETAIL, category: 'vendor_detail' },
      ];

      for (const mapping of dynamicRouteMappings) {
        if (matchPath(mapping.pattern, path)) {
          pageviewProperties.route_category = mapping.category;
          break;
        }
      }

      // Capture pageview event with metadata
      posthog.capture('$pageview', pageviewProperties);
    }
  }, [path, posthog]);

  const navigate = (to: string | number, options?: { replace?: boolean }) => {
    // Handle browser history navigation (back/forward)
    if (typeof to === 'number') {
      logger.debug('[AppRouter] Browser history navigation', { module: 'AppRouter', action: 'history_navigation', direction: to > 0 ? 'forward' : 'back', steps: Math.abs(to) });
      try {
        window.history.go(to);
      } catch (_e) {
        logger.warn('History navigation failed (sandboxed env)', { module: 'AppRouter', action: 'history_navigation' });
      }
      // Let popstate event handle the path update
      return;
    }

    let historyUrl = to;
    const pathnameInitial = to.split('?')[0].split('#')[0];
    const searchFromTo = to.includes('?') ? to.slice(to.indexOf('?')) : '';
    let pathnameForState = pathnameInitial;

    if (isAuthenticated && pathnameInitial.startsWith('/app')) {
      const r = resolveCanonicalUrl({
        pathname: pathnameInitial,
        search: searchFromTo,
        user: { isSelfManagingOwner: Boolean(isSelfManagingOwner) },
      });
      if (r.action === 'redirect') {
        historyUrl = `${r.pathname}${r.search}`;
        pathnameForState = r.pathname.split('?')[0].split('#')[0];
      }
    }

    // Handle path-based navigation (single writer — `writeHistory` + dev guards)
    logger.debug('[AppRouter] Navigating', { to: historyUrl, module: 'AppRouter', action: 'navigate', replace: options?.replace });
    try {
      writeHistory(() => {
        if (options?.replace) {
          window.history.replaceState(null, '', historyUrl);
        } else {
          window.history.pushState(null, '', historyUrl);
        }
      });
    } catch (_e) {
      logger.warn('Navigation history update failed (sandboxed env), falling back to memory routing.', { module: 'AppRouter', action: 'navigate_history_fallback' });
    }
    setPath(pathnameForState);
  };

  // --- ROUTE CONFIGURATION ---
  let mainContent = null;
  let layoutContent = null;
  let routeParams: Record<string, string> = {};

  const match = (pattern: string) => {
    const result = matchPath(pattern, path);
    if (result && result.isExact) {
      routeParams = result.params;
      return true;
    }
    return false;
  };

  if (path === '/demo') {
    if (isDemoMode() && DemoPersonaPicker) {
      mainContent = (
        <Suspense fallback={<RouteLoading />}>
          <DemoPersonaPicker />
        </Suspense>
      );
    } else {
      mainContent = <Navigate to={ROUTES.LOGIN} replace />;
    }
  } else if (path === ROUTES.LOGIN) {
    mainContent = <ClerkLoginView />;
  } else if (path === ROUTES.SIGN_UP) {
    if (clerkSignedIn) {
      mainContent = <Navigate to={ROUTES.COMPLETE_ONBOARDING} replace />;
    } else {
      mainContent = <ClerkSignUpView />;
    }
  } else if (path === ROUTES.COMPLETE_ONBOARDING) {
    mainContent = (
      <OperatorFirstRunShell step="profile">
        <CompleteOnboardingView />
      </OperatorFirstRunShell>
    );
  } else if (path === ROUTES.SELECT_ORG) {
    if (isAuthenticated) {
      mainContent = (
        <OperatorFirstRunShell step="workspace">
          <Suspense fallback={<RouteLoading />}>
            <OrgSelectionView />
          </Suspense>
        </OperatorFirstRunShell>
      );
    } else {
      mainContent = <Navigate to={ROUTES.LOGIN} replace />;
    }
  } else if (path === ROUTES.PUBLIC_SITE) {
    mainContent = <Suspense fallback={<RouteLoading />}><PublicWebsiteView onBack={() => navigate(ROUTES.LOGIN)} /></Suspense>;
  } else if (match(ROUTES.PUBLIC_APPLY)) {
    const { listingId } = routeParams;
    mainContent = <Suspense fallback={<RouteLoading />}><PublicApplicationForm listingId={listingId} onCancel={() => navigate(ROUTES.PUBLIC_SITE)} onComplete={() => navigate(`${ROUTES.PUBLIC_SITE}?applied=true`)} /></Suspense>;
  } else if (path === ROUTES.PUBLIC_PAY_RENT) {
    mainContent = <Navigate to={ROUTES.LOGIN} replace />;
  } else if (path === ROUTES.PUBLIC_MAINTENANCE_REQUEST) {
    const PublicMaintenanceRequestView = lazy(() => import('./components/maintenance/PublicMaintenanceRequestView').then(m => ({ default: m.PublicMaintenanceRequestView })));
    mainContent = <Suspense fallback={<RouteLoading />}><PublicMaintenanceRequestView onBack={() => navigate(ROUTES.PUBLIC_SITE)} /></Suspense>;
  } else if (path === ROUTES.PUBLIC_SCHEDULE_TOUR) {
    const propertyId = typeof window === 'undefined' ? undefined : new URLSearchParams(window.location.search).get('propertyId') ?? undefined;
    const PublicScheduleTourView = lazy(() => import('./components/leasing/PublicScheduleTourView').then(m => ({ default: m.PublicScheduleTourView })));
    mainContent = <Suspense fallback={<RouteLoading />}><PublicScheduleTourView onBack={() => navigate(ROUTES.PUBLIC_SITE)} propertyId={propertyId ?? undefined} /></Suspense>;
  } else if (match(ROUTES.PUBLIC_QUESTIONNAIRE)) {
    const { leadId } = routeParams;
    const PublicQuestionnaireView = lazy(() => import('./components/leasing/PublicQuestionnaireView').then(m => ({ default: m.PublicQuestionnaireView })));
    mainContent = leadId ? <Suspense fallback={<RouteLoading />}><PublicQuestionnaireView leadId={leadId} onBack={() => navigate(ROUTES.PUBLIC_SITE)} /></Suspense> : null;
  } else if (path === ROUTES.LEGAL_PRIVACY) {
    mainContent = <Suspense fallback={<RouteLoading />}><PrivacyPolicyView /></Suspense>;
  } else if (path === ROUTES.LEGAL_TERMS) {
    mainContent = <Suspense fallback={<RouteLoading />}><TermsOfServiceView /></Suspense>;
  } else if (path === ROUTES.LEGAL_RETENTION) {
    mainContent = <Suspense fallback={<RouteLoading />}><RetentionPolicyView /></Suspense>;
  } else if (path === ROUTES.LEGAL_SUBPROCESSORS) {
    mainContent = <Suspense fallback={<RouteLoading />}><SubprocessorsView /></Suspense>;
  } else if (path === ROUTES.ROOT || path === '') {
    const hasProfile = isWebTest ? hasBackendProfile : clerkSignedIn && hasBackendProfile;
    if (hasProfile) {
      mainContent = <Navigate to={ROUTES.APP} replace />;
    } else {
      mainContent = <LandingView />;
    }
  } else if (typeof path === 'string' && path.startsWith(ROUTES.APP)) {
    if (isAuthenticated) {
      mainContent = (
        <AuthGuard shell>
          <Layout />
        </AuthGuard>
      );
    } else if (!isWebTest && clerkSignedIn && isAuthPending) {
      // Clerk session is active but `getCurrentUser` has not finished — avoid bouncing to
      // `/login` + `<SignIn forceRedirectUrl="/app">` which would infinite-loop until profile loads.
      mainContent = (
        <div className="flex min-h-screen items-center justify-center bg-slate-900">
          <LoadingSpinner size="lg" />
        </div>
      );
    } else {
      mainContent = <Navigate to={ROUTES.LOGIN} replace />;
    }

    // --- APP ROUTES (Rendered into Layout Outlet) ---

    if (path === ROUTES.APP) {
      layoutContent = <RoleBasedHome />;
    }
    else if (path === ROUTES.APP_ACTIVITY) {
      layoutContent = <ActivityView />;
    }

    // Shared - require authentication for all app routes
    else if (path === ROUTES.COMMUNICATIONS) layoutContent = <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.PM, ROLES.TENANT, ROLES.OWNER, ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><CommunicationsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.NOTES) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><NotesListView /></Suspense></AuthGuard>;
    else if (path === ROUTES.SETTINGS) {
      const { userRole } = useAppStore.getState();
      if (userRole === ROLES.OWNER) {
        layoutContent = <AuthGuard allowedRoles={[ROLES.OWNER]}><Suspense fallback={<RouteLoading />}><OwnerSettingsView /></Suspense></AuthGuard>;
      } else if (userRole === ROLES.TENANT) {
        layoutContent = <AuthGuard allowedRoles={[ROLES.TENANT]}><Suspense fallback={<RouteLoading />}><TenantSettingsView /></Suspense></AuthGuard>;
      } else if (userRole === ROLES.VENDOR) {
        layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorSettingsView /></Suspense></AuthGuard>;
      } else {
        layoutContent = <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.PLATFORM_ADMIN, ROLES.PM, ROLES.SYSTEM_ADMIN]}><Suspense fallback={<RouteLoading />}><SettingsView userRole={userRole} /></Suspense></AuthGuard>;
      }
    }
    else if (path === ROUTES.VENDOR_SETTINGS) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorSettingsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.INTEGRATIONS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><IntegrationSettingsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.ADMIN_AUDIT) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><AdminAuditView /></Suspense></AuthGuard>;
    else if (path === ROUTES.UNAUTHORIZED) layoutContent = <UnauthorizedView />;

    // Leasing
    else if (path === ROUTES.LEASING) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><LeasingDashboardView /></Suspense></AuthGuard>;
    else if (path === ROUTES.GUEST_CARDS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><GuestCardsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.APPLICATIONS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><ApplicationsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.LEASES) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><LeaseManagementView /></Suspense></AuthGuard>;
    else if (path === ROUTES.LEASING_RENEWALS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><LeaseManagementView /></Suspense></AuthGuard>;
    else if (path === ROUTES.LEASING_ONBOARDING) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><LeasingOnboardingView /></Suspense></AuthGuard>;
    else if (path === ROUTES.MARKETING) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><MarketingDashboardView onViewPublicSite={() => window.location.href = ROUTES.PUBLIC_SITE} /></Suspense></AuthGuard>;

    // Maintenance
    else if (path === ROUTES.MAINTENANCE) layoutContent = <AuthGuard allowedRoles={[ROLES.OWNER, ...MANAGEMENT_ROLES]}><Suspense fallback={<RouteLoading />}><MaintenanceView /></Suspense></AuthGuard>;
    else if (path === ROUTES.MAINTENANCE_COMPLETED) layoutContent = <AuthGuard allowedRoles={[ROLES.OWNER, ...MANAGEMENT_ROLES]}><Suspense fallback={<RouteLoading />}><MaintenanceView defaultView="completed" /></Suspense></AuthGuard>;
    else if (path === ROUTES.PROJECTS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><ProjectsView /></Suspense></AuthGuard>;
    else if (match(ROUTES.PROJECT_DETAIL)) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><ProjectDetailView /></Suspense></AuthGuard>;
    else if (path === ROUTES.TURNS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><TurnBoardView /></Suspense></AuthGuard>;
    else if (path === ROUTES.INSPECTIONS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><InspectionsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.INSPECTION_SCHEDULES) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><InspectionSchedulingView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDORS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><VendorDirectoryView /></Suspense></AuthGuard>;
    else if (match(ROUTES.VENDOR_DETAIL)) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><VendorProfileView /></Suspense></AuthGuard>;
    else if (path === ROUTES.GAMIFICATION) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><VendorMarketplace /></Suspense></AuthGuard>;
    else if (path === ROUTES.SOPS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><SOPGenerator /></Suspense></AuthGuard>;

    // Accounting
    else if (path === ROUTES.OPS_ACCOUNTING_BILLS)
      layoutContent = (
        <AuthGuard allowedRoles={MANAGEMENT_ROLES}>
          <Suspense fallback={<RouteLoading />}>
            <AccountingBillsView />
          </Suspense>
        </AuthGuard>
      );
    else if (path === ROUTES.RECURRING_BILLS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><RecurringBillsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.RECEIVABLES) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><AccountingPaymentsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.ACCOUNTING_PAYMENT_PLANS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><AccountingPaymentsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.GL) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><GeneralLedgerView /></Suspense></AuthGuard>;
    else if (path === ROUTES.COA) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><ChartOfAccountsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.JOURNAL) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><JournalEntryForm onBack={() => navigate(ROUTES.GL)} /></Suspense></AuthGuard>;
    else if (path === ROUTES.RECONCILIATION) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><BankReconciliationView /></Suspense></AuthGuard>;
    else if (path === ROUTES.ACCOUNTING_BANK_OPS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><BankReconciliationView /></Suspense></AuthGuard>;
    else if (path === ROUTES.ACCOUNTING_PURCHASE_ORDERS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><AccountingBillsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.ACCOUNTING_THREE_WAY_MATCH) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><AccountingBillsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.CAM) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><CAMTrackingView /></Suspense></AuthGuard>;
    else if (path === ROUTES.CLOSE) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><MonthEndCloseView /></Suspense></AuthGuard>;
    else if (path === ROUTES.ACCOUNTING_MANAGEMENT_FEE_SWEEP)
      layoutContent = (
        <AuthGuard allowedRoles={MANAGEMENT_ROLES}>
          <Suspense fallback={<RouteLoading />}>
            <ManagementFeeSweepView />
          </Suspense>
        </AuthGuard>
      );
    else if (path === ROUTES.ACCOUNTING_OWNER_DISTRIBUTION)
      layoutContent = (
        <AuthGuard allowedRoles={MANAGEMENT_ROLES}>
          <Suspense fallback={<RouteLoading />}>
            <OwnerDistributionView />
          </Suspense>
        </AuthGuard>
      );
    else if (path === ROUTES.ACCOUNTING_DISTRIBUTIONS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><DistributionsListView /></Suspense></AuthGuard>;
    else if (path === ROUTES.DOCUMENTS_LIBRARY) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><DocumentLibraryView /></Suspense></AuthGuard>;

    // Properties - order matters: specific routes before dynamic ones
    else if (path === ROUTES.PROPERTIES) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><PropertyDirectoryView /></Suspense></AuthGuard>;
    else if (path === ROUTES.PROPERTY_ONBOARD) layoutContent = <Navigate to={`${ROUTES.PROPERTIES}?openAddPropertyWizard=1`} replace />;
    else if (path === ROUTES.PROPERTY_OWNERS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES} blockSelfManagingOwner><Suspense fallback={<RouteLoading />}><OwnerAccessView /></Suspense></AuthGuard>;
    else if (path === ROUTES.PROPERTY_UNITS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><UnitManagementView /></Suspense></AuthGuard>;
    else if (path === ROUTES.PROPERTY_PORTFOLIOS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><PortfolioView /></Suspense></AuthGuard>;
    else if (match(ROUTES.PROPERTY_DETAIL)) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><PropertyDetailsView /></Suspense></AuthGuard>;

    // Tenants
    else if (path === ROUTES.TENANTS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><TenantDirectoryView /></Suspense></AuthGuard>;
    else if (match(ROUTES.TENANT_DETAIL)) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><TenantProfileView /></Suspense></AuthGuard>;

    // Owners (PM back office view) — SMO cannot access
    else if (path === ROUTES.OWNERS_MANAGEMENT) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES} blockSelfManagingOwner><Suspense fallback={<RouteLoading />}><OwnerAccessView /></Suspense></AuthGuard>;

    // Owner Portal Routes (canonical `/app/owner/...`; legacy `/app/owners/<portal>/...` redirects in resolveCanonicalUrl)
    else if (path === ROUTES.OWNER_STATEMENTS) layoutContent = <AuthGuard allowedRoles={[ROLES.OWNER, ...MANAGEMENT_ROLES]}><Suspense fallback={<RouteLoading />}><OwnerStatementsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.OWNER_PROPERTIES || path === ROUTES.OWNER_BACKOFFICE_PROPERTIES) layoutContent = <AuthGuard allowedRoles={[ROLES.OWNER, ...MANAGEMENT_ROLES]}><Suspense fallback={<RouteLoading />}><PropertyDirectoryView /></Suspense></AuthGuard>;
    else if (match(ROUTES.OWNER_PROPERTY_DETAIL) || match(ROUTES.OWNER_BACKOFFICE_PROPERTY_DETAIL)) layoutContent = <AuthGuard allowedRoles={[ROLES.OWNER, ...MANAGEMENT_ROLES]}><Suspense fallback={<RouteLoading />}><PropertyDetailsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.OWNER_REPORTING) layoutContent = <AuthGuard allowedRoles={[ROLES.OWNER, ...MANAGEMENT_ROLES]}><Suspense fallback={<RouteLoading />}><ReportsLibraryView /></Suspense></AuthGuard>;

    // Admin / Ops
    else if (path === ROUTES.WORKFLOWS) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><WorkflowsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.PM_CALENDAR) layoutContent = <AuthGuard allowedRoles={[ROLES.PM, ROLES.ADMIN]}><Suspense fallback={<RouteLoading />}><PMCalendarView /></Suspense></AuthGuard>;
    else if (path === ROUTES.PM_WORK_QUEUE) layoutContent = <AuthGuard allowedRoles={[ROLES.PM, ROLES.ADMIN]}><Suspense fallback={<RouteLoading />}><WorkQueueView /></Suspense></AuthGuard>;
    else if (path === ROUTES.PM_EXCEPTIONS) layoutContent = <AuthGuard allowedRoles={[ROLES.PM, ROLES.ADMIN]}><Suspense fallback={<RouteLoading />}><ExceptionQueueView /></Suspense></AuthGuard>;
    else if (path === ROUTES.REPORTING) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><ReportsLibraryView /></Suspense></AuthGuard>;
    else if (path === ROUTES.REPORTING_SCHEDULED) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES}><Suspense fallback={<RouteLoading />}><ScheduledReportsView /></Suspense></AuthGuard>;
    // Compliance - Full structure (all roles can access, components gate actions internally)
    else if (path === ROUTES.COMPLIANCE) layoutContent = <AuthGuard allowedRoles={[...MANAGEMENT_ROLES, ROLES.OWNER]}><Suspense fallback={<RouteLoading />}><ComplianceOverviewView /></Suspense></AuthGuard>;
    else if (path === ROUTES.COMPLIANCE_INSPECTIONS) layoutContent = <AuthGuard allowedRoles={[...MANAGEMENT_ROLES, ROLES.OWNER]}><Suspense fallback={<RouteLoading />}><ComplianceInspectionsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.COMPLIANCE_HCV) layoutContent = <AuthGuard allowedRoles={[...MANAGEMENT_ROLES, ROLES.OWNER]}><Suspense fallback={<RouteLoading />}><ComplianceHCVView /></Suspense></AuthGuard>;
    else if (path === ROUTES.COMPLIANCE_OBLIGATIONS) layoutContent = <AuthGuard allowedRoles={[...MANAGEMENT_ROLES, ROLES.OWNER]}><Suspense fallback={<RouteLoading />}><ComplianceObligationsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.COMPLIANCE_GOVERNANCE) layoutContent = <AuthGuard allowedRoles={[ROLES.ADMIN, ROLES.PM]}><Suspense fallback={<RouteLoading />}><GovernanceLogsView /></Suspense></AuthGuard>;

    // Tenant Portal
    else if (path === ROUTES.MY_HOME) layoutContent = <AuthGuard allowedRoles={[ROLES.TENANT]}><RoleBasedHome /></AuthGuard>;
    else if (path === ROUTES.MY_PAYMENTS) layoutContent = <AuthGuard allowedRoles={[ROLES.TENANT]}><Suspense fallback={<RouteLoading />}><TenantPaymentsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.MY_MAINTENANCE) layoutContent = <AuthGuard allowedRoles={[ROLES.TENANT]}><Suspense fallback={<RouteLoading />}><MaintenanceView /></Suspense></AuthGuard>;
    else if (path === ROUTES.MY_DOCUMENTS) layoutContent = <AuthGuard allowedRoles={[ROLES.TENANT]}><Suspense fallback={<RouteLoading />}><TenantDocumentsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.MY_COMMUNICATION) layoutContent = <AuthGuard allowedRoles={[ROLES.TENANT]}><Suspense fallback={<RouteLoading />}><TenantCommunicationView /></Suspense></AuthGuard>;
    else if (path === ROUTES.MY_SETTINGS) layoutContent = <AuthGuard allowedRoles={[ROLES.TENANT]}><Suspense fallback={<RouteLoading />}><TenantSettingsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.ONBOARDING) layoutContent = <AuthGuard allowedRoles={[ROLES.TENANT]}><Suspense fallback={<RouteLoading />}><TenantOnboardingView /></Suspense></AuthGuard>;
    else if (path === ROUTES.LEASE_RENEWAL) layoutContent = <AuthGuard allowedRoles={[ROLES.TENANT]}><Suspense fallback={<RouteLoading />}><TenantLeaseWizard onComplete={() => navigate(ROUTES.APP)} onCancel={() => navigate(ROUTES.APP)} /></Suspense></AuthGuard>;
    else if (path === ROUTES.COMMUNITY) {
      const { userRole } = useAppStore.getState();
      layoutContent = <AuthGuard allowedRoles={[ROLES.TENANT, ROLES.PM]}><Suspense fallback={<RouteLoading />}><CommunityView userRole={userRole} /></Suspense></AuthGuard>;
    }

    // Vendor Portal
    else if (match(ROUTES.VENDOR_JOB_DETAIL)) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorJobView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_JOBS) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorJobsOverviewView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_JOBS_ACTIVE) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorActiveJobsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_JOBS_COMPLETED) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorJobHistoryView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_JOBS_HISTORY) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR, ...MANAGEMENT_ROLES]}><Suspense fallback={<RouteLoading />}><VendorJobHistoryView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_PROFILE) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorPortfolioView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_PHOTOS) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorWorkPhotosView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_CERTIFICATIONS) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorCertificationsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_FINANCIALS_INVOICES) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorInvoicesView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_FINANCIALS_PAYMENTS) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorPaymentsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_FINANCIALS_EARNINGS) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorEarningsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_FINANCIALS) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorFinancialsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_CALENDAR) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorCalendarView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_PERFORMANCE) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorPerformanceView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_PROFILE_BUSINESS) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorBusinessInfoView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_PROFILE_REVIEWS) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorReviewsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_MAP) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorMapView /></Suspense></AuthGuard>;
    else if (path === ROUTES.VENDOR_COMMUNICATION) layoutContent = <AuthGuard allowedRoles={[ROLES.VENDOR]}><Suspense fallback={<RouteLoading />}><VendorCommunicationView /></Suspense></AuthGuard>;

    // Owner Portal
    else if (path === ROUTES.OWNER_DASHBOARD) layoutContent = <AuthGuard allowedRoles={[ROLES.OWNER]}><RoleBasedHome /></AuthGuard>;
    else if (path === ROUTES.OWNER_COMMUNICATION) layoutContent = <AuthGuard allowedRoles={[ROLES.OWNER]}><Suspense fallback={<RouteLoading />}><OwnerCommunicationView /></Suspense></AuthGuard>;
    else if (path === ROUTES.OWNER_DOCUMENTS) layoutContent = <AuthGuard allowedRoles={[ROLES.OWNER]}><Suspense fallback={<RouteLoading />}><OwnerDocumentsView /></Suspense></AuthGuard>;
    else if (path === ROUTES.OWNER_SETTINGS) layoutContent = <AuthGuard allowedRoles={[ROLES.OWNER]}><Suspense fallback={<RouteLoading />}><OwnerSettingsView /></Suspense></AuthGuard>;

    // Owner detail (PM back-office) — dynamic `/app/owners/:ownerId`; MUST stay AFTER every literal
    // `/app/owners/*` route above (otherwise `:ownerId = "statements"`/"dashboard"/... swallows them
    // and bounces SMOs to /app/unauthorized via the blockSelfManagingOwner guard).
    else if (match(ROUTES.OWNER_DETAIL)) layoutContent = <AuthGuard allowedRoles={MANAGEMENT_ROLES} blockSelfManagingOwner><Suspense fallback={<RouteLoading />}><OwnerProfileView /></Suspense></AuthGuard>;

    // Investor Portal — launch-deferred (no live routes until launch work resumes).

    // Fallback: unknown /app path → 404 (not Unauthorized)
    else layoutContent = <NotFoundView />;

  } else {
    // Unknown route, redirect to login
    logger.warn('[AppRouter] Unknown path, redirecting to login', { module: 'AppRouter', action: 'unknown_path_redirect', path });
    mainContent = <Navigate to={ROUTES.LOGIN} replace />;
  }

  return (
    <RouterContext.Provider value={{ path, navigate, params: routeParams, routeContent: layoutContent }}>
      <PublicRouteThemeSync />
      {mainContent}
    </RouterContext.Provider>
  );
};

